import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  companyReviews: [], // GET /staff-monthly-review/company — one page of staff + reviewed flag + review for the given month
  companyReviewsTotal: 0,
  companyReviewsMonth: null,
  companyReviewsStats: { totalStaff: 0, reviewedCount: 0, pendingCount: 0, averageRating: null },
  history: [], // GET /staff-monthly-review/:employeeId/history
  companyHistory: [], // GET /staff-monthly-review/company/history — one page
  companyHistoryTotal: 0,
  companyHistoryStaffOptions: [], // everyone with at least one review, for the staff filter
  loading: false,
  historyLoading: false,
  companyHistoryLoading: false,
  submitting: false,
  submitError: null
};

export const fetchCompanyMonthlyReviews = createAsyncThunk(
  'staffMonthlyReview/fetchCompany',
  // params: { month?, page, limit, search }
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/staff-monthly-review/company', { params });
      return response.data; // { month, staff, recordsTotal, stats }
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const submitStaffMonthlyReview = createAsyncThunk(
  'staffMonthlyReview/submit',
  // `criteria` : [{ key, score }], une note par critère de la zone d'activité.
  async ({ employeeId, criteria, comment, month }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/staff-monthly-review/${employeeId}`, {
        criteria,
        comment,
        month
      });
      return { employeeId, review: response.data, month: month || null };
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const fetchStaffMonthlyReviewHistory = createAsyncThunk(
  'staffMonthlyReview/fetchHistory',
  async (employeeId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/staff-monthly-review/${employeeId}/history`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const fetchCompanyMonthlyReviewHistory = createAsyncThunk(
  'staffMonthlyReview/fetchCompanyHistory',
  // params: { page, limit, employeeId? }
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/staff-monthly-review/company/history', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

const staffMonthlyReviewSlice = createSlice({
  name: 'staffMonthlyReview',
  initialState,
  reducers: {
    clearSubmitError: (state) => {
      state.submitError = null;
    },
    clearHistory: (state) => {
      state.history = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyMonthlyReviews.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanyMonthlyReviews.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.companyReviews = payload.staff || [];
        state.companyReviewsTotal = payload.recordsTotal || 0;
        state.companyReviewsMonth = payload.month || null;
        state.companyReviewsStats = payload.stats || initialState.companyReviewsStats;
      })
      .addCase(fetchCompanyMonthlyReviews.rejected, (state) => {
        state.loading = false;
      })

      .addCase(submitStaffMonthlyReview.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
      })
      .addCase(submitStaffMonthlyReview.fulfilled, (state, { payload }) => {
        state.submitting = false;
        // The lifetime average changes whenever any month gets a new review, regardless
        // of which month is currently displayed.
        state.companyReviews = state.companyReviews.map((staff) => {
          if (staff._id !== payload.employeeId) return staff;
          const priorTotal = (staff.overallAverageRating ?? 0) * staff.overallReviewCount;
          const overallReviewCount = staff.overallReviewCount + 1;
          const overallAverageRating = parseFloat(
            ((priorTotal + (payload.review?.averageRating ?? 0)) / overallReviewCount).toFixed(2)
          );
          return { ...staff, overallAverageRating, overallReviewCount };
        });
        // Only patch the currently-displayed month's "reviewed" status — a backfilled
        // review for a different month shouldn't flip the chip shown here.
        if (payload.month === state.companyReviewsMonth) {
          const wasReviewed = state.companyReviews.find((s) => s._id === payload.employeeId)?.reviewed;
          state.companyReviews = state.companyReviews.map((staff) =>
            staff._id === payload.employeeId
              ? { ...staff, reviewed: true, review: payload.review }
              : staff
          );
          if (!wasReviewed) {
            const stats = state.companyReviewsStats;
            const priorTotal = (stats.averageRating ?? 0) * stats.reviewedCount;
            const reviewedCount = stats.reviewedCount + 1;
            stats.reviewedCount = reviewedCount;
            stats.pendingCount = Math.max(stats.pendingCount - 1, 0);
            stats.averageRating = parseFloat(
              ((priorTotal + (payload.review?.averageRating ?? 0)) / reviewedCount).toFixed(2)
            );
          }
        }
      })
      .addCase(submitStaffMonthlyReview.rejected, (state, { payload }) => {
        state.submitting = false;
        state.submitError = payload?.message || 'Failed to submit review';
      })

      .addCase(fetchStaffMonthlyReviewHistory.pending, (state) => {
        state.historyLoading = true;
      })
      .addCase(fetchStaffMonthlyReviewHistory.fulfilled, (state, { payload }) => {
        state.historyLoading = false;
        state.history = payload || [];
      })
      .addCase(fetchStaffMonthlyReviewHistory.rejected, (state) => {
        state.historyLoading = false;
      })

      .addCase(fetchCompanyMonthlyReviewHistory.pending, (state) => {
        state.companyHistoryLoading = true;
      })
      .addCase(fetchCompanyMonthlyReviewHistory.fulfilled, (state, { payload }) => {
        state.companyHistoryLoading = false;
        state.companyHistory = payload.data || [];
        state.companyHistoryTotal = payload.recordsTotal || 0;
        state.companyHistoryStaffOptions = payload.staffOptions || [];
      })
      .addCase(fetchCompanyMonthlyReviewHistory.rejected, (state) => {
        state.companyHistoryLoading = false;
      });
  }
});

export const { clearSubmitError, clearHistory } = staffMonthlyReviewSlice.actions;

export default staffMonthlyReviewSlice.reducer;
